import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService }        from '@ngx-translate/core';

import { ImportFailureGroupModel } from './typings/imports.summary';
import { ImportRunSummaryModel }   from './typings/imports.summary';
import { ImportsService }          from './imports.service';
import { MessagesService }         from '../messages/messages.service';

// WS-6752. Between confirming the mapping and importing, show the operator what is
// about to happen. Until now they pressed import and found out afterwards, by which
// point part of the file had been written and then partly destroyed.
//
// Everything shown here comes from the check that runs before anything is written, so
// reaching this screen has cost nothing and going back from it costs nothing either.
@Component({
  selector   : 'wor-import-summary',
  styles: [ require('./import.summary.component.scss') ],
  template: require('./import.summary.component.html')
})
export class ImportSummaryComponent implements OnInit {
  // Rows in the operator's file, counted by the screen that parsed it. The backend
  // reports what will not import; how many that leaves is only knowable with the total.
  @Input() totalRows = 0;

  // WS-6807. Set when the run has parked a second time, so this screen is showing a
  // question the operator has not seen rather than the one they just answered. The two
  // look nearly identical, so without saying so the screen reads as unresponsive.
  @Input() isFollowUp = false;

  @Output() back      = new EventEmitter<void>();
  @Output() proceeded = new EventEmitter<void>();

  summary   : ImportRunSummaryModel;
  decisions : { [ answerKey : string ] : string } = {};

  loading    = false;
  submitting = false;

  // WS-6819. True only while the file is being fetched, so the button cannot be pressed
  // twice and produce two downloads of the same rows.
  downloading = false;

  constructor (
    protected importsService  : ImportsService,
    protected messagesService : MessagesService,
    protected translateService: TranslateService
  ) {}

  // WS-6807. Nothing else fetches this. The page around this component shows it with an
  // *ngIf, so mounting is the only moment it can load, and its own template is hidden
  // until the summary arrives. Without this it was shown, fetched nothing, and drew
  // nothing, which is why no held back row could ever be answered and why the whole
  // resolution step was unreachable from the moment it was built.
  //
  // Being tied to mounting is also what makes a second round work. The page hides this
  // while the run is moving, so a run that parks again unmounts and remounts, and loads
  // again on its own.
  ngOnInit () : void {
    this.refresh();
  }

  get groups () : Array<ImportFailureGroupModel> {
    return this.summary?.groups || [];
  }

  get hasProblems () : boolean {
    return this.groups.length > 0;
  }

  get isAwaitingDecisions () : boolean {
    return this.summary?.status === 'awaiting_decisions';
  }

  get isFinished () : boolean {
    return this.summary?.status === 'finished';
  }

  // What this group's answer is recorded against. Taken from the backend, which sends it
  // per group. The fallback keeps this screen working against a backend that has not
  // shipped WS-6802 yet, where the only groups with anything to answer are the ones
  // answered per reference type.
  answerKeyFor ( group : ImportFailureGroupModel ) : string {
    return group.answer_key || group.reference_type;
  }

  // WS-6802. Whether there is anything to say about this group at all. A group that can
  // neither be created nor imported without its unusable values is described and left
  // alone, because an answer that cannot help is worse than no answer.
  canAnswer ( group : ImportFailureGroupModel ) : boolean {
    // WS-6980. A group asking which accepted value an unrecognised word means is answerable
    // whether or not a suggestion arrived: the accepted values are the answer, and the
    // suggestion only decides what starts selected.
    return group.can_create || group.can_import_without || !!group.can_repair || this.canChoose(group);
  }

  // WS-6980. Whether this group offers a pick from the accepted values. Requires the values
  // themselves, because a picker with nothing in it is a question nobody can answer, and
  // showing one would block proceeding with no way forward.
  canChoose ( group : ImportFailureGroupModel ) : boolean {
    return !!group.can_choose && this.choicesFor(group).length > 0;
  }

  choicesFor ( group : ImportFailureGroupModel ) : Array<{ code : string; name? : string }> {
    return group.choices || [];
  }

  // The operator's own word, which is what makes the question answerable: they are choosing
  // a translation for something they wrote, not identifying a code from nothing.
  wordFor ( group : ImportFailureGroupModel ) : string {
    return group.choose_value || '';
  }

  fieldBeingChosen ( group : ImportFailureGroupModel ) : string {
    return group.choose_field || '';
  }

  // WS-6980. Too many unrecognised values in one column to be a vocabulary mismatch. Read
  // from a flag rather than from the description, which is written for a person and will be
  // reworded.
  looksWrong ( group : ImportFailureGroupModel ) : boolean {
    return !!group.looks_wrong;
  }

  // What is currently selected for a choosable group. Empty when nothing is, which is a
  // legitimate state: the rows stay held, exactly as skipping leaves them.
  chosenFor ( group : ImportFailureGroupModel ) : string {
    const answer = this.decisions[ this.answerKeyFor(group) ];

    return this.isKeyword(answer) ? '' : ( answer || '' );
  }

  // The four keywords the other answers use. A choosable group's answer is an accepted code,
  // so anything that is not one of these is a chosen value.
  protected isKeyword ( answer : string ) : boolean {
    return [ 'create', 'skip', 'import_without', 'repair' ].indexOf(answer) !== -1;
  }

  // WS-6980. Records the pick. An empty pick clears the answer rather than storing a blank,
  // so the group counts as unanswered and its rows stay held, which is what the operator
  // asked for by clearing it.
  chooseValue ( group : ImportFailureGroupModel, code : string ) : void {
    if ( !this.canChoose(group) ) {
      return;
    }

    const key = this.answerKeyFor(group);

    if ( code ) {
      this.decisions[ key ] = code;
    }
    else {
      delete this.decisions[ key ];
    }
  }

  // WS-6980. These rows come in with the value the operator chose, which is why they stop
  // counting as held back.
  willChoose ( group : ImportFailureGroupModel ) : boolean {
    return this.canChoose(group) && !!this.chosenFor(group);
  }

  // Groups the operator could answer but has not. Proceeding while any remain would
  // leave the run parked, so the button says what is still needed rather than appearing
  // to do nothing.
  get unansweredGroups () : Array<ImportFailureGroupModel> {
    return this.groups.filter(group => this.canAnswer(group) && !this.decisions[ this.answerKeyFor(group) ]);
  }

  get canProceed () : boolean {
    return !this.submitting && this.unansweredGroups.length === 0;
  }

  // Held back given the answers so far. A group the operator has chosen to create is no
  // longer held: those rows import once the missing records exist. Nor is a group they
  // have chosen to import without the value that was wrong: those rows come in short of
  // it, which is still coming in.
  get heldBackCount () : number {
    return this.groups.reduce(
      ( total, group ) =>
        // WS-6976. A group we are putting right is not held either. Those rows come in
        // with the value corrected, which is more than coming in short of it.
        // WS-6980. Nor is a group whose unrecognised word the operator has translated. Those
        // rows come in with the accepted value in place of their own word.
        total + ( this.willCreate(group) || this.willImportWithout(group) || this.willRepair(group) || this.willChoose(group) ? 0 : group.count ), 0);
  }

  get willImportCount () : number {
    return Math.max(this.totalRows - this.heldBackCount, 0);
  }

  // WS-6785. Rows that came in, as the RUN recorded them: created outright, plus matched
  // against something already here.
  //
  // This used to return willImportCount, the browser side prediction above, under a name
  // in the past tense. A prediction is the right answer before the import and the wrong
  // one after it, because the write pass refuses rows the check could not know about and
  // a cancelled run writes fewer still. On Staging on 11 September a twelve row file with
  // eleven rows held showed 1 imported on a run that wrote nothing at all, with the
  // backend's own truthful result_message printed directly above it.
  //
  // The fallback is for a backend that predates the counts being sent, not for a finished
  // run: hasRunCounts is true on every finished run against a current backend.
  get importedCount () : number {
    if ( !this.hasRunCounts ) {
      return this.willImportCount;
    }

    // Coerced rather than added as they arrive. These are integer columns today, so they
    // come back as JSON numbers, but Rails sends a decimal column as a STRING and the
    // failure if one of these ever became decimal is silent and on brand for this ticket:
    // "3" + "5" is "35" on a screen whose entire defect was reporting a wrong number.
    return Number(this.summary.number_created || 0) + Number(this.summary.number_matched || 0);
  }

  // WS-6785. Rows the run did not take, as the run recorded them. heldBackCount above
  // stays as it is and stays correct, because before the import nothing has happened yet
  // and the groups are all there is to go on.
  get skippedCount () : number {
    if ( !this.hasRunCounts ) {
      return this.heldBackCount;
    }

    return Number(this.summary.number_skipped || 0);
  }

  // Null on every run that has not finished, and zero is a legitimate value on one that
  // has, so this tests for absence rather than for truthiness.
  get hasRunCounts () : boolean {
    return this.summary != null && this.summary.number_created != null;
  }

  willCreate ( group : ImportFailureGroupModel ) : boolean {
    return group.can_create && this.decisions[ this.answerKeyFor(group) ] === 'create';
  }

  // WS-6802. The whole point of the ticket: these rows come in, with the value that could
  // not be used left empty and everything else intact.
  willImportWithout ( group : ImportFailureGroupModel ) : boolean {
    return group.can_import_without && this.decisions[ this.answerKeyFor(group) ] === 'import_without';
  }

  // WS-6976. The whole point of the ticket: these rows come in with the value put right,
  // rather than coming in without it or not coming in at all.
  willRepair ( group : ImportFailureGroupModel ) : boolean {
    return !!group.can_repair && this.decisions[ this.answerKeyFor(group) ] === 'repair';
  }

  // Which field is being corrected, ready to read. Named for the operator, because "the
  // phone number" is what they recognise and the column name is not.
  fieldBeingRepaired ( group : ImportFailureGroupModel ) : string {
    return group.repair_field || '';
  }

  // A real value from their own file, and what it becomes. The backend puts genuine
  // examples in the group, so the operator is shown their own data rather than a made up
  // illustration of it.
  repairExample ( group : ImportFailureGroupModel ) : string {
    return ( group.examples || [] )[ 0 ] || '';
  }

  willSkip ( group : ImportFailureGroupModel ) : boolean {
    return this.canAnswer(group) && this.decisions[ this.answerKeyFor(group) ] === 'skip';
  }

  // Which fields go in empty, ready to read. Listed rather than described, because "and
  // the phone number as well" is the difference between accepting this and going back to
  // fix the file.
  fieldsLeftOut ( group : ImportFailureGroupModel ) : string {
    return ( group.import_without_fields || [] ).join(', ');
  }

  choose ( group : ImportFailureGroupModel, answer : string ) : void {
    if ( !this.canAnswer(group) ) {
      return;
    }

    this.decisions[ this.answerKeyFor(group) ] = answer;
  }

  // WS-6819. Hands back the rows the import could not take, as a file the operator can
  // correct and drop straight back in. The whole recovery loop is this one button plus
  // the reasons already on screen above it.
  //
  // Fetched rather than linked. A plain link would leave the application's session
  // behind and the endpoint would refuse it, and window.open on a blob loses the
  // filename, which matters here: the operator ends up with several of these in a
  // downloads folder and needs to know which import each came from.
  downloadHeldRows () : Promise<void> {
    if ( this.downloading ) {
      return Promise.resolve();
    }

    this.downloading = true;

    return this.importsService.fetchHeldRows()
    .then(({ blob, filename }) => {
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href     = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Released on the next tick rather than immediately: revoking it before the click
      // has been serviced cancels the download on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    })
    .catch(() => {
      this.messagesService.error(
        this.translateService.instant('IMPORT_SPACE.SUMMARY_DOWNLOAD_FAILED'));
    })
    .finally(() => {
      this.downloading = false;
    });
  }

  refresh () : Promise<void> {
    this.loading = true;

    return this.importsService.fetchRunSummary()
    .then(summary => {
      this.summary = summary;
      this.seedSuggestions();
    })
    .finally(() => {
      this.loading = false;
    });
  }

  // WS-6980, AC 3. The suggestion arrives PRE-SELECTED, which is the whole point of this
  // ticket: the operator confirms a match rather than hunting for one in a list of a hundred
  // and sixty six.
  //
  // Seeded into decisions here rather than shown at render time, on purpose. The picker binds
  // to chosenFor, which reads the decisions hash, and that hash is what gets submitted. A
  // suggestion that was displayed but not seeded would show the operator a selection they
  // never made and then submit nothing for it, which is worse than opening blank.
  //
  // An answer the operator has already given is never overwritten, so this is safe when a
  // parked run is polled again or the component remounts between rounds, which it does: the
  // page hides this while the run moves, so a run that parks twice mounts this twice.
  //
  // A group the backend sent no suggestion for is left blank, which is the other half of AC 3
  // and AC 9 of the paired ticket. A value we could not guess is still one they can tell us.
  protected seedSuggestions () : void {
    this.groups.forEach(group => {
      if ( !this.canChoose(group) ) {
        return;
      }

      if ( this.decisions[ this.answerKeyFor(group) ] ) {
        return;
      }

      if ( !group.suggested_value ) {
        return;
      }

      // The suggestion and the list of choices are produced in two different places: the
      // suggestion when the file was checked, the choices when this screen asked. The
      // backend guarantees a suggestion is an accepted value, but only against the list as
      // it stood then. Seeding one the picker has no option for would show a blank control
      // while submitting a value, which is the one outcome worse than showing nothing.
      if ( !this.choicesFor(group).some(choice => choice.code === group.suggested_value) ) {
        return;
      }

      this.chooseValue(group, group.suggested_value);
    });
  }

  goBack () : void {
    this.back.emit();
  }

  proceed () : Promise<void> {
    if ( !this.canProceed ) {
      return Promise.resolve();
    }

    this.submitting = true;

    // Only groups with something to answer are sent. The rest have nothing to answer, and
    // sending them would invent an answer the operator never gave.
    const answers = this.groups
      .filter(group => this.canAnswer(group) && this.decisions[ this.answerKeyFor(group) ])
      .reduce(( sent, group ) => {
        sent[ this.answerKeyFor(group) ] = this.decisions[ this.answerKeyFor(group) ];

        return sent;
      }, {} as { [ answerKey : string ] : string });

    return this.importsService.applyImportDecisions(answers)
    .then(() => {
      this.proceeded.emit();
    })
    .finally(() => {
      this.submitting = false;
    });
  }
}
